import { sendAnalytics } from '@audio-network/analytics'
import {
	Button,
	Modal,
	ModalActions,
	ModalContent,
	ModalHeading,
	Paragraph,
} from '@audio-network/componentlibrary'
import { useTranslation } from 'next-i18next'
import { toast } from 'react-toastify'

import { useActiveProject } from '@/hooks/useActiveProject'

import { DeleteTracksToast } from '../Toasts'

type Props = {
	tracks: Track[]
	id: string
	source?: string
	/** Called after a successful delete (e.g. clear track selection in the list). */
	onDeleteSuccess?: () => void
}

const deleteTracksModalHeadingId = 'delete-tracks-modal-heading'
const deleteTracksModalDescriptionId = 'delete-tracks-modal-description'

const DeleteTracksModal = ({
	id,
	tracks,
	source = 'Edit Project Tray',
	onDeleteSuccess,
}: Props): JSX.Element => {
	const { project, onTracksDelete } = useActiveProject()
	const { t } = useTranslation(['projectedit'])

	const handleConfirm = async (): Promise<void> => {
		if (!onTracksDelete) {
			return
		}

		const response = await onTracksDelete(tracks)

		if (response) {
			onDeleteSuccess?.()
			sendAnalytics({
				noun: 'Delete Tracks',
				pastTenseVerb: 'Completed',
				eventParameters: {
					TrackSource: source,
				},
			})
			toast.success(
				<DeleteTracksToast tracks={tracks} projectName={project?.Name ?? ''} />
			)
		} else {
			toast.error(
				<DeleteTracksToast
					tracks={tracks}
					projectName={project?.Name ?? ''}
					error
				/>
			)
		}
	}

	const heading =
		tracks.length > 1
			? t('delete-tracks-modal.multi-track-heading')
			: t('delete-tracks-modal.single-track-heading')

	const description =
		tracks.length > 1
			? t('delete-tracks-modal.multi-track-description')
			: t('delete-tracks-modal.single-track-description')

	return (
		<Modal
			aria-describedby={deleteTracksModalDescriptionId}
			aria-labelledby={deleteTracksModalHeadingId}
			id={id}
		>
			<ModalHeading id={deleteTracksModalHeadingId}>{heading}</ModalHeading>

			<ModalContent>
				<Paragraph id={deleteTracksModalDescriptionId}>{description}</Paragraph>
			</ModalContent>

			<ModalActions>
				<Button
					command="close"
					commandFor={id}
					onClick={handleConfirm}
					type="button"
					variant="primary"
				>
					{t('delete-tracks-modal.confirmation')}
				</Button>
				<Button
					command="close"
					commandFor={id}
					type="button"
					variant="secondary"
				>
					{t('delete-tracks-modal.decline')}
				</Button>
			</ModalActions>
		</Modal>
	)
}

export default DeleteTracksModal
